import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { ROUTE_NAMES } from '@/lib/enums';
import { ROLE_125_ADMIN } from '@/util/roles';

export const routes = [
	{
		path: '',
		name: ROUTE_NAMES.ONETWOFIVEDATA.OVERVIEW,
		component: () => {
			return import('@/views/125data/Overview.vue');
		},
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Overview',
			breadcrumb: 'Overview',
		},
	},
	{
		path: 'companies',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Companies',
			breadcrumb: 'Companies',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.COMPANIES.OVERVIEW,
				component: () => {
					return import('@/views/125data/companies/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Companies',
					breadcrumb: 'Companies',
				},
			},
			{
				path: ':companyId',
				name: ROUTE_NAMES.ONETWOFIVEDATA.COMPANIES.VIEW_COMPANY,
				component: () => {
					return import('@/views/125data/companies/View-Company.vue');
				},
				props: (route: RouteLocationNormalizedLoaded) => {
					return {
						companyId: route.params.companyId as string,
					};
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Company',
					breadcrumb: 'Company',
				},
			},
		],
	},
	{
		path: 'listings',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Listings',
			breadcrumb: 'Listings',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.LISTINGS.OVERVIEW,
				component: () => {
					return import('@/views/125data/listings/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Listings',
					// Empty so the crumb trail reads "125 Data > Listings" rather than
					// repeating the parent's label. Matches the reports index.
					breadcrumb: '',
				},
			},
			{
				path: 'google-locations',
				name: ROUTE_NAMES.ONETWOFIVEDATA.LISTINGS.GOOGLE_LOCATIONS,
				component: () => {
					return import('@/views/125data/listings/GoogleLocations.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Listings - Google managed listings',
					breadcrumb: 'Google managed listings',
				},
			},
		],
	},
	{
		path: 'snippets',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Snippets',
			breadcrumb: 'Snippets',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.SNIPPETS.OVERVIEW,
				component: () => {
					return import('@/views/125data/snippets/Overview.vue');
				},
				meta: {
					requireAuth: true,
					// Empty so the crumb trail reads "125 Data > Snippets". Matches listings.
					breadcrumb: '',
				},
			},
		],
	},
	{
		path: 'feedback',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Feedback',
			breadcrumb: 'Feedback',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.FEEDBACK.OVERVIEW,
				// Dashboard.vue is the hub; Overview.vue beside it is the browse page
				// below. See the file comment in Dashboard.vue for why they are that way
				// round.
				component: () => {
					return import('@/views/125data/feedback/Dashboard.vue');
				},
				meta: {
					requireAuth: true,
					// No title: `to.meta` is the merged meta of every matched record, so
					// the parent's title still sets the document title. Carrying one here
					// would also give the crumb builder something to fall back to, and an
					// empty breadcrumb only drops the crumb when there is no title behind
					// it. Matches the depot index routes.
					breadcrumb: '',
				},
			},
			{
				// The path repeats its parent because that is the URL asked for. The
				// route name, the breadcrumb and the nav label deliberately do not:
				// "Feedback > Feedback" in a crumb trail reads as a builder repeating
				// itself rather than as a page.
				path: 'feedback',
				name: ROUTE_NAMES.ONETWOFIVEDATA.FEEDBACK.BROWSE,
				component: () => {
					return import('@/views/125data/feedback/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Feedback - All Feedback',
					breadcrumb: 'All feedback',
				},
			},
			{
				path: 'top-comments',
				name: ROUTE_NAMES.ONETWOFIVEDATA.FEEDBACK.TOP_COMMENTS,
				component: () => {
					return import('@/views/125data/feedback/TopComments.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Feedback - Top Comments',
					breadcrumb: 'Top comments',
				},
			},
		],
	},
	{
		path: 'reports',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Reports',
			breadcrumb: 'Reports',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.REPORTS.OVERVIEW,
				component: () => {
					return import('@/views/125data/reports/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Reports',
					// Empty so the crumb trail reads "125 Data > Reports" rather than
					// repeating the parent's label. Matches the depot index routes.
					breadcrumb: '',
				},
			},
			{
				path: 'catalogue',
				name: ROUTE_NAMES.ONETWOFIVEDATA.REPORTS.CATALOGUE,
				component: () => {
					return import('@/views/125data/reports/Catalogue.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Reports - Catalogue',
					breadcrumb: 'Catalogue',
				},
			},
			{
				path: 'builder',
				name: ROUTE_NAMES.ONETWOFIVEDATA.REPORTS.BUILDER,
				component: () => {
					return import('@/views/125data/reports/Builder.vue');
				},
				// The only route in the section above the 125.User baseline. Note this
				// is a product decision about who may build ad-hoc reports, not a write
				// boundary: POST /reports/builder/query is a POST used for a read,
				// because the report definition will not fit a query string.
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Reports - Report Builder',
					breadcrumb: 'Report Builder',
					minRole: ROLE_125_ADMIN,
				},
			},
		],
	},
	{
		path: 'chatbots',
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Chatbots',
			breadcrumb: 'Chatbots',
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.CHAT.OVERVIEW,
				component: () => {
					return import('@/views/125data/chatbots/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Chatbots',
					breadcrumb: 'Chatbots',
				},
			},
			{
				path: 'faqs',
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Chatbots - FAQs',
					breadcrumb: 'FAQs',
				},
				children: [
					{
						path: '',
						name: ROUTE_NAMES.ONETWOFIVEDATA.CHAT.FAQS.OVERVIEW,
						component: () => {
							return import('@/views/125data/chatbots/faqs/Overview.vue');
						},
					},
					{
						path: 'chat/:conversationId?',
						name: ROUTE_NAMES.ONETWOFIVEDATA.CHAT.FAQS.FAQS_CHAT,
						component: () => {
							return import('@/views/125data/chatbots/faqs/Chatbot.vue');
						},
						props: (route: RouteLocationNormalizedLoaded) => {
							return {
								conversationId: route.params.conversationId as string | undefined,
							};
						},
						meta: {
							requireAuth: true,
							title: 'OneTwoFive - Chatbots - FAQs - Chat',
							breadcrumb: 'Chat',
						},
					},
				],
			},
		],
	},
	{
		path: 'integrations',
		// Declared once on the parent so every page in the section, including ones added
		// later, needs 125.Admin: these pages read and (later) mint credentials for the
		// public API.
		meta: {
			requireAuth: true,
			title: 'OneTwoFive - Integrations',
			breadcrumb: 'Integrations',
			minRole: ROLE_125_ADMIN,
		},
		children: [
			{
				path: '',
				name: ROUTE_NAMES.ONETWOFIVEDATA.INTEGRATIONS.OVERVIEW,
				component: () => {
					return import('@/views/125data/integrations/Overview.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Integrations',
					breadcrumb: '',
				},
			},
			{
				path: 'list',
				name: ROUTE_NAMES.ONETWOFIVEDATA.INTEGRATIONS.LIST,
				component: () => {
					return import('@/views/125data/integrations/List.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Integrations - All integrations',
					breadcrumb: 'All integrations',
				},
			},
			{
				path: 'templates',
				name: ROUTE_NAMES.ONETWOFIVEDATA.INTEGRATIONS.TEMPLATES,
				component: () => {
					return import('@/views/125data/integrations/Templates.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Integrations - Templates',
					breadcrumb: 'Templates',
				},
			},
			{
				path: 'new',
				name: ROUTE_NAMES.ONETWOFIVEDATA.INTEGRATIONS.NEW,
				component: () => {
					return import('@/views/125data/integrations/New.vue');
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Integrations - New integration',
					breadcrumb: 'New integration',
				},
			},
			{
				// GUID only, so a sibling like `templates` can never be read as a client id,
				// and a malformed id falls through to the 404 page without a request.
				path: ':clientId([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})',
				name: ROUTE_NAMES.ONETWOFIVEDATA.INTEGRATIONS.VIEW,
				component: () => {
					return import('@/views/125data/integrations/View-Integration.vue');
				},
				props: (route: RouteLocationNormalizedLoaded) => {
					return {
						clientId: route.params.clientId as string,
					};
				},
				meta: {
					requireAuth: true,
					title: 'OneTwoFive - Integration',
					breadcrumb: 'Integration',
				},
			},
		],
	},
];
